import { motion } from 'framer-motion'

// ─── Star rating ──────────────────────────────────────────────────────────────
function StarRating({ rating = 5 }) {
  return (
    <div className="flex items-center gap-0.5" aria-label={`${rating} out of 5 stars`}>
      {Array.from({ length: 5 }, (_, i) => (
        <span
          key={i}
          className={[
            'text-lg leading-none transition-colors',
            i < rating ? 'text-secondary' : 'text-ink-light',
          ].join(' ')}
        >
          ★
        </span>
      ))}
    </div>
  )
}

/**
 * TestimonialCard
 *
 * Props:
 *   clientName  — display name
 *   clientRole  — e.g. "Happy Mom"
 *   reviewText  — the review body
 *   emoji       — avatar emoji (default: 😊)
 *   rating      — 1–5 (default: 5)
 *   index       — stagger animation delay
 */
export default function TestimonialCard({
  clientName,
  clientRole  = 'Happy Parent',
  reviewText,
  emoji       = '😊',
  rating      = 5,
  index       = 0,
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-40px' }}
      transition={{ delay: index * 0.15, duration: 0.55, ease: 'easeOut' }}
      whileHover={{ y: -4 }}
      className="bg-surface rounded-3xl p-7 shadow-card hover:shadow-glow-primary transition-all duration-300 flex flex-col"
    >
      {/* Top row: quote icon + stars */}
      <div className="flex items-start justify-between mb-5">
        {/* Large decorative quote */}
        <span
          className="font-accent text-6xl leading-none text-primary/25 select-none"
          aria-hidden="true"
        >
          ❝
        </span>
        <StarRating rating={rating} />
      </div>

      {/* Review text */}
      <p className="text-ink-muted text-sm font-body leading-relaxed italic flex-1 mb-6">
        "{reviewText}"
      </p>

      {/* Client info */}
      <div className="flex items-center gap-3 pt-5 border-t border-primary-100">
        {/* Emoji avatar */}
        <div className="w-11 h-11 rounded-full bg-gradient-hero flex items-center justify-center text-xl shrink-0">
          {emoji}
        </div>
        <div>
          <p className="font-display text-ink text-sm leading-tight">{clientName}</p>
          <p className="text-ink-light text-xs font-body mt-0.5">{clientRole}</p>
        </div>
      </div>
    </motion.div>
  )
}
